/**
 * Knowledge Base 출처 데이터.
 * 백엔드는 메시지에 structuredRdbContent, structuredQuery, unstructuredReport 를 평탄하게 내려준다.
 * 화면은 이를 knowledgeContent 하나로 묶어 쓰고, 형식이 틀린 값은 여기서 걸러 화면이 깨지지 않게 한다.
 */

/** Structured > RDB. Data Source, Filters, Aggregation 세 섹션 */
export interface KbStructuredRdbItem {
  reference_number: KbReferenceNumber;
  data_source?: string[];
  filters?: string[];
  aggregation?: string[];
}

/** Structured > SQL query */
export interface KbStructuredQueryItem {
  reference_number: KbReferenceNumber;
  query: string;
}

/** Unstructured. 문서 출처와 본문 조각 */
export interface KbUnstructuredItem {
  reference_number: KbReferenceNumber;
  title: string;
  url?: string;
  /** 구 형식. 조각 하나 */
  chunk?: string;
  /** 신 형식. 같은 문서의 여러 조각. 있으면 chunk 보다 우선 */
  chunks?: string[];
}

export interface KbContentData {
  structuredRdbContent?: KbStructuredRdbItem[] | null;
  structuredQuery?: KbStructuredQueryItem[] | null;
  unstructuredReport?: KbUnstructuredItem[] | null;
}

/** 본문 인용 번호. 숫자로 오거나, 본문 태그와 같은 글자("RDB", "A")로 온다 */
export type KbReferenceNumber = number | string;

type UnknownRecord = Record<string, unknown>;

const isRecord = (v: unknown): v is UnknownRecord =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const hasReferenceNumber = (v: UnknownRecord) =>
  (typeof v.reference_number === "number" && Number.isFinite(v.reference_number)) ||
  (typeof v.reference_number === "string" && v.reference_number.trim() !== "");

/** 문자열 하나로 오면 배열로 감싸고, 문자열이 아닌 원소는 버린다. */
const toStringList = (v: unknown): string[] | undefined => {
  if (typeof v === "string") return [v];
  if (!Array.isArray(v)) return undefined;
  return v.filter((s): s is string => typeof s === "string");
};

/** href 로 그리므로 http(s) 만 남긴다. javascript:, data: 를 막는다. */
const toSafeUrl = (v: unknown): string | undefined =>
  typeof v === "string" && /^https?:\/\//i.test(v) ? v : undefined;

const toList = <T>(v: unknown, toItem: (r: UnknownRecord) => T | null): T[] => {
  if (!Array.isArray(v)) return [];
  return v
    .filter(isRecord)
    .filter(hasReferenceNumber)
    .map(toItem)
    .filter((item): item is T => item !== null);
};

const withDefined = <T extends object>(item: T): T =>
  Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined)) as T;

const toRdbItem = (r: UnknownRecord): KbStructuredRdbItem =>
  withDefined({
    reference_number: r.reference_number as KbReferenceNumber,
    data_source: toStringList(r.data_source),
    filters: toStringList(r.filters),
    aggregation: toStringList(r.aggregation),
  });

const toQueryItem = (r: UnknownRecord): KbStructuredQueryItem | null =>
  typeof r.query === "string"
    ? { reference_number: r.reference_number as KbReferenceNumber, query: r.query }
    : null;

const toUnstructuredItem = (r: UnknownRecord): KbUnstructuredItem | null =>
  typeof r.title === "string"
    ? withDefined({
        reference_number: r.reference_number as KbReferenceNumber,
        title: r.title,
        url: toSafeUrl(r.url),
        chunk: typeof r.chunk === "string" ? r.chunk : undefined,
        chunks: toStringList(r.chunks),
      })
    : null;

export const hasKnowledgeContent = (kc?: KbContentData | null): kc is KbContentData =>
  kc !== null &&
  kc !== undefined &&
  ((kc.structuredRdbContent?.length ?? 0) > 0 ||
    (kc.structuredQuery?.length ?? 0) > 0 ||
    (kc.unstructuredReport?.length ?? 0) > 0);

/**
 * 메시지의 KB 필드를 검사해 knowledgeContent 로 묶은 새 객체를 돌려준다.
 * 이미 knowledgeContent 가 있으면 그 값을, 없으면 평탄 필드를 읽는다. 입력은 바꾸지 않는다.
 */
export const normalizeKnowledgeContent = <T>(msg: T): T & { knowledgeContent?: KbContentData } => {
  if (!isRecord(msg)) return msg as T & { knowledgeContent?: KbContentData };
  const source = isRecord(msg.knowledgeContent) ? msg.knowledgeContent : msg;
  const content: KbContentData = {};
  const rdb = toList(source.structuredRdbContent, toRdbItem);
  const query = toList(source.structuredQuery, toQueryItem);
  const unstructured = toList(source.unstructuredReport, toUnstructuredItem);
  if (rdb.length > 0) content.structuredRdbContent = rdb;
  if (query.length > 0) content.structuredQuery = query;
  if (unstructured.length > 0) content.unstructuredReport = unstructured;

  const rest = Object.fromEntries(Object.entries(msg).filter(([key]) => key !== "knowledgeContent"));
  return (hasKnowledgeContent(content) ? { ...rest, knowledgeContent: content } : rest) as T & {
    knowledgeContent?: KbContentData;
  };
};

/** 본문 태그가 열어 달라고 지정하는 탭. [A]/[B] 는 unstructured, [RDB] 는 rdb */
export type KbCitationTab = "unstructured" | "rdb";

export type CitationMainTab = "references" | "knowledge-base";
export type CitationKbTab = "structured" | "unstructured";
export type CitationStructuredTab = "rdb" | "sql-query";

/**
 * 처음 열 탭. 본문 태그가 지정한 탭이 있으면 그쪽, 없으면 자료가 있는 첫 탭이다.
 * 우선순위는 웹 > RDB > SQL > Unstructured 이고, 아무것도 없으면 External Ref.
 */
export const resolveInitialCitationTabs = ({
  citationTab = null,
  hasWeb,
  hasRdb,
  hasSql,
  hasUnstructured,
}: {
  citationTab?: KbCitationTab | null;
  hasWeb: boolean;
  hasRdb: boolean;
  hasSql: boolean;
  hasUnstructured: boolean;
}): { main: CitationMainTab; kb: CitationKbTab; structured: CitationStructuredTab } => {
  if (citationTab !== null) {
    return {
      main: "knowledge-base",
      kb: citationTab === "unstructured" ? "unstructured" : "structured",
      structured: "rdb",
    };
  }
  if (hasWeb) return { main: "references", kb: "structured", structured: "rdb" };
  if (hasRdb) return { main: "knowledge-base", kb: "structured", structured: "rdb" };
  if (hasSql) return { main: "knowledge-base", kb: "structured", structured: "sql-query" };
  if (hasUnstructured) return { main: "knowledge-base", kb: "unstructured", structured: "rdb" };
  return { main: "references", kb: "structured", structured: "rdb" };
};

/** 이스케이프된 줄바꿈(\n, \r\n)을 실제 줄바꿈으로. 남아 있으면 KaTeX 가 수식을 못 그린다. */
const unescapeChunk = (s: string): string =>
  s.replace(/\\r\\n/g, "\n").replace(/\\[nr]/g, "\n");

/** 화면에 그릴 조각 목록. chunks 를 먼저 쓰고, 없으면 chunk 하나, 빈 조각은 뺀다. */
export const buildChunkEntries = (item: KbUnstructuredItem): string[] => {
  const raw = item.chunks ?? (item.chunk !== undefined ? [item.chunk] : []);
  return raw.filter((text) => text !== "").map(unescapeChunk);
};
